import React, { useEffect, useCallback } from "react";

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  headerExtra, // optional controls shown next to the close button
  hideClose = false, // drop the X (backdrop click and Esc still close)
  icon,
  children,
  footer,
  size = "md",
  tall = false, // a big popup: at least 40rem high (to 90vh), centred; the body is the one scroll
  className = "",
}) => {
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && onClose) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const SIZES = {
    sm: "max-w-md",
    md: "max-w-2xl",
    "3xl": "max-w-3xl", // the Edit session popup's width
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal panel */}
      <div
        className={`
          relative w-full ${SIZES[size] || SIZES.md}
          ${tall ? "max-h-[90vh] min-h-[min(90vh,40rem)] flex flex-col" : ""}
          bg-surface-card rounded-modal shadow-elevation-3
          animate-fadeIn
          ${className}
        `.trim()}
      >
        {/* Header */}
        {title && (
          <div className="flex flex-wrap items-start gap-3 px-6 py-4 border-b border-surface-border">
            {icon && (
              <div className="flex-shrink-0 mt-0.5">{icon}</div>
            )}
            <div className="flex-1 min-w-[8rem]">
              <h2 className="text-subtitle text-content-primary m-0">{title}</h2>
              {subtitle && (
                <p className="text-caption text-content-secondary m-0 mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
            {headerExtra && (
              <div className="flex-shrink-0 flex items-center gap-2">{headerExtra}</div>
            )}
            {!hideClose && (
              <button
                onClick={onClose}
                className="flex-shrink-0 p-1 text-content-tertiary hover:text-content-primary rounded-button hover:bg-surface-hover transition-colors"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M13.1459 0.333271C13.0534 0.240567 12.9435 0.167019 12.8225 0.116838C12.7016 0.0666565 12.5719 0.0408263 12.4409 0.0408263C12.31 0.0408263 12.1803 0.0666565 12.0593 0.116838C11.9383 0.167019 11.8284 0.240567 11.7359 0.333271L6.84593 5.21327L1.95593 0.32327C1.86335 0.230688 1.75343 0.157248 1.63247 0.107143C1.51151 0.0570384 1.38186 0.03125 1.25093 0.03125C1.12 0.03125 0.990347 0.0570384 0.869383 0.107143C0.748419 0.157248 0.638508 0.230688 0.545927 0.32327C0.453345 0.415852 0.379905 0.525763 0.3298 0.646727C0.279695 0.767691 0.253906 0.89734 0.253906 1.02827C0.253906 1.1592 0.279695 1.28885 0.3298 1.40981C0.379905 1.53078 0.453345 1.64069 0.545927 1.73327L5.43593 6.62327L0.545927 11.5133C0.453345 11.6059 0.379905 11.7158 0.3298 11.8367C0.279695 11.9577 0.253906 12.0873 0.253906 12.2183C0.253906 12.3492 0.279695 12.4788 0.3298 12.5998C0.379905 12.7208 0.453345 12.8307 0.545927 12.9233C0.638508 13.0159 0.748419 13.0893 0.869383 13.1394C0.990347 13.1895 1.12 13.2153 1.25093 13.2153C1.38186 13.2153 1.51151 13.1895 1.63247 13.1394C1.75343 13.0893 1.86335 13.0159 1.95593 12.9233L6.84593 8.03327L11.7359 12.9233C11.8285 13.0159 11.9384 13.0893 12.0594 13.1394C12.1803 13.1895 12.31 13.2153 12.4409 13.2153C12.5719 13.2153 12.7015 13.1895 12.8225 13.1394C12.9434 13.0893 13.0533 13.0159 13.1459 12.9233C13.2385 12.8307 13.3119 12.7208 13.3621 12.5998C13.4122 12.4788 13.4379 12.3492 13.4379 12.2183C13.4379 12.0873 13.4122 11.9577 13.3621 11.8367C13.3119 11.7158 13.2385 11.6059 13.1459 11.5133L8.25593 6.62327L13.1459 1.73327C13.5259 1.35327 13.5259 0.713271 13.1459 0.333271Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={`px-6 py-4 overflow-y-auto ${tall ? "flex-1 min-h-0" : "max-h-[70vh]"}`}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-surface-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
